import { sortBy, startCase } from "lodash";
import React, { FC, useState } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgCloseButton,
  IgDivider,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  GenericBillingPlanDto,
  OrganizationDto,
  PostCustomBillingUrlRequestBodyType,
  SubscriptionBillingProvider,
  SubscriptionBillingProviderList,
  SubscriptionInterval,
  SubscriptionIntervalList,
} from "@intelligems/ig-types";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Debug } from "@src/utils/Debug";

import { useCancelBilling } from "../../../api/organization-subscriptions/cancel-billing";
import { useSubscriptions } from "../../../api/organization-subscriptions/get-subscriptions";
import { useAppSubscriptions } from "../../../api/subscriptions/get-app-subscriptions";
import { useGetCustomBillingUrl } from "../../../api/subscriptions/get-custom-billing-url";
import { ClipboardCopy } from "../../../utils/ClipboardCopy";
import { CancelButton } from "../CancelButton";

interface CustomBillingProps {
  organization: OrganizationDto;
}

interface CancelBillingButtonProps {
  appSubscription: GenericBillingPlanDto;
}

const CancelBillingButton = ({ appSubscription }: CancelBillingButtonProps) => {
  const cancelBilling = useCancelBilling();

  const handleYesClick = () => {
    if (appSubscription.provider === "shopify") {
      cancelBilling.mutate({
        body: {
          id: appSubscription.id,
        },
        pathParams: undefined,
        queryParams: undefined,
      });
    } else {
      window.open(
        `https://dashboard.stripe.com/subscriptions/${appSubscription.id}`,
        "_blank"
      );
    }
  };

  return (
    <CancelButton
      keyId={"cancel-billing"}
      handleYesClick={handleYesClick}
      isLoading={cancelBilling.isPending}
    />
  );
};

interface CancelBillingSectionProps {
  provider: SubscriptionBillingProvider;
}

const CancelBillingSection = ({ provider }: CancelBillingSectionProps) => {
  const appSubscriptions = useAppSubscriptions({
    refetchOnWindowFocus: "always",
  });

  return (
    <Grid container item direction={"column"}>
      <Grid item xs>
        <IgTypography variant="h6">
          Active {capitalizeFirstLetter(provider)} Subscriptions
        </IgTypography>
      </Grid>
      <Grid container item direction={"column"}>
        {appSubscriptions.isSuccess &&
        appSubscriptions.data[
          provider === "shopify" ? "shopifyPlans" : "stripePlans"
        ]?.length ? (
          appSubscriptions.data[
            provider === "shopify" ? "shopifyPlans" : "stripePlans"
          ].map((appSubscription) => (
            <Grid
              container
              item
              key={appSubscription.id}
              alignItems={"center"}
              direction={"row"}
              columnSpacing={2}
            >
              <Grid item alignItems={"center"}>
                {appSubscription.name}
              </Grid>
              <Grid item>
                <CancelBillingButton appSubscription={appSubscription} />
              </Grid>
            </Grid>
          ))
        ) : (
          <IgTypography>
            No active {capitalizeFirstLetter(provider)} subscriptions
          </IgTypography>
        )}
      </Grid>
    </Grid>
  );
};

const regex = /(\d+)K.*/;
export const CustomBilling: FC<CustomBillingProps> = ({ organization }) => {
  const [selectedToAdd, setSelectedToAdd] = useImmer<string>("");
  const [customBillingPayload, setCustomBillingPayload] =
    useImmer<PostCustomBillingUrlRequestBodyType>({
      interval: "monthly",
      customPrices: {},
      freeTrialLength: 0, // not in use
      test: false,
      billingProvider: "shopify",
    });
  const [freeTrialLength, setFreeTrialLength] = useImmer<string>("0");

  const getCustomBilling = useGetCustomBillingUrl(organization.id);
  const subscriptions = useSubscriptions({
    orgId: organization.id,
    all: true,
    queryOptions: {
      refetchOnWindowFocus: "always",
    },
  });

  if (subscriptions.isSuccess) {
    return (
      <Grid container direction={"column"} rowSpacing={0}>
        <CancelBillingSection provider={"shopify"} />
        <IgDivider spacing={"s"} />
        <CancelBillingSection provider={"stripe"} />
        <IgDivider spacing={"s"} />
        <Grid container item direction={"column"} rowSpacing={2}>
          <Grid item xs>
            <IgTypography variant="h6">Custom Billing Link</IgTypography>
          </Grid>
          <Grid item>
            <FormControl>
              <Typography variant={"body2"}>Billing Provider</Typography>
              <IgSelect
                sx={{ width: "200px" }}
                value={customBillingPayload.billingProvider}
                onChange={(e) => {
                  setCustomBillingPayload((draft) => {
                    draft.billingProvider = e.target
                      .value as SubscriptionBillingProvider;
                    return draft;
                  });
                }}
              >
                {Object.values(SubscriptionBillingProviderList).map(
                  (provider, index) => (
                    <MenuItem value={provider} key={index}>
                      {startCase(provider)}
                    </MenuItem>
                  )
                )}
              </IgSelect>
            </FormControl>
          </Grid>
          <Grid
            container
            item
            direction={"row"}
            columnSpacing={2}
            alignItems={"end"}
          >
            <Grid item>
              <FormControl>
                <Typography variant={"body2"}>Interval</Typography>
                <IgSelect
                  sx={{ width: "200px" }}
                  value={customBillingPayload.interval}
                  onChange={(e) => {
                    setCustomBillingPayload((draft) => {
                      draft.interval = e.target.value as SubscriptionInterval;
                      return draft;
                    });
                  }}
                >
                  {Object.values(SubscriptionIntervalList).map(
                    (interval, index) => (
                      <MenuItem value={interval} key={index}>
                        {interval}
                      </MenuItem>
                    )
                  )}
                </IgSelect>
              </FormControl>
            </Grid>
            <Grid item>
              <IgTextField
                label={`Every X ${
                  customBillingPayload.interval === "annual"
                    ? "Years"
                    : "Months"
                }`}
                disabled={customBillingPayload.billingProvider === "shopify"}
                value={customBillingPayload.intervalCount || 1}
                onChange={(e) => {
                  setCustomBillingPayload((draft) => {
                    draft.intervalCount = parseFloat(e.target.value);
                    return draft;
                  });
                }}
              />
            </Grid>
            <Grid item>
              <IgTextField
                label={"Free Trial Length"}
                inputProps={{
                  min: 0,
                  inputMode: "numeric",
                  pattern: "^[0-9][\\.\\d]*(,\\d+)?$",
                }}
                value={freeTrialLength ?? ""}
                type={"text"}
                onChange={(e) => {
                  if (e.target.validity.valid) {
                    setFreeTrialLength(e.target.value);
                  }
                }}
              />
            </Grid>
            <Grid item sx={{ paddingBottom: "2px", width: "210px" }}>
              <IgButton
                disabled={
                  !Object.keys(customBillingPayload.customPrices).length ||
                  Object.values(customBillingPayload.customPrices).some(
                    (v) => v === 0
                  )
                }
                loading={getCustomBilling.isPending}
                variant="contained"
                onClick={() => {
                  getCustomBilling.mutate({
                    body: {
                      ...customBillingPayload,
                      freeTrialLength: parseFloat(freeTrialLength),
                    },
                    pathParams: undefined,
                    queryParams: undefined,
                  });
                }}
              >
                Generate Billing Link
              </IgButton>
            </Grid>
            <Grid item xs sx={{ paddingBottom: "2px" }}>
              <FormControlLabel
                sx={{ fontSize: "12px" }}
                control={
                  <IgCheckbox
                    name={"Is Test"}
                    checked={customBillingPayload.test}
                    onClick={() => {
                      setCustomBillingPayload((draft) => {
                        draft.test = !draft.test;
                        return draft;
                      });
                    }}
                  />
                }
                label={<IgTypography variant={"body2"}>Is Test</IgTypography>}
              />
            </Grid>
          </Grid>
          <Grid container item direction={"column"} rowSpacing={1}>
            <Grid
              container
              item
              columnSpacing={2}
              rowSpacing={1}
              alignItems={"end"}
            >
              <Grid item>
                <FormControl>
                  <IgTypography variant={"body2"}>Plan</IgTypography>
                  <IgSelect
                    sx={{ width: "600px" }}
                    value={selectedToAdd}
                    onChange={(e: SelectChangeEvent<any>) => {
                      setSelectedToAdd(e.target.value);
                    }}
                  >
                    {sortBy(
                      subscriptions.data.allSubscriptions
                        .filter(
                          (sub) =>
                            (sub.defaultInterval ===
                              customBillingPayload.interval &&
                              sub.version === "2024-02") ||
                            sub.name === "PAUSE PLAN"
                        )
                        .map((sub) => ({
                          ...sub,
                          name: `${sub.name.split(";")[0]} - ${sub.title} (${
                            sub.defaultInterval
                          })`,
                        })),
                      (_) => Number(_.name.match(regex)?.[1] || "99")
                    ).map((subscription, index) => (
                      <MenuItem value={subscription.id} key={subscription.id}>
                        {subscription.name}
                      </MenuItem>
                    ))}
                  </IgSelect>
                </FormControl>
              </Grid>
              <Grid item sx={{ paddingBottom: "2px" }}>
                <IgButton
                  variant={"contained"}
                  disabled={
                    Object.keys(customBillingPayload.customPrices).length >= 1
                  }
                  onClick={() => {
                    setCustomBillingPayload((draft) => {
                      draft.customPrices[selectedToAdd] = 0;
                      return draft;
                    });
                  }}
                >
                  Add
                </IgButton>
              </Grid>
            </Grid>
            {Object.keys(customBillingPayload.customPrices).map((id) => {
              return (
                <Grid
                  container
                  item
                  key={id}
                  alignItems={"center"}
                  columnSpacing={2}
                >
                  <Grid item>
                    <IgTypography variant={"body2"}>&nbsp;</IgTypography>
                    <IgCloseButton
                      onClick={() => {
                        setCustomBillingPayload((draft) => {
                          delete draft.customPrices[id];
                          return draft;
                        });
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <IgTextField
                      label={"Name"}
                      disabled={true}
                      value={
                        subscriptions.data.allSubscriptions.find(
                          (s) => s.id === id
                        )!.title
                      }
                    />
                  </Grid>
                  <Grid item>
                    <IgTextField
                      label={"Price"}
                      value={customBillingPayload[id]}
                      onChange={(e) => {
                        setCustomBillingPayload((draft) => {
                          draft.customPrices[id] = parseFloat(e.target.value);
                          return draft;
                        });
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid item sx={{ marginTop: "10px" }}>
          {getCustomBilling.data?.billingUrl && (
            <ClipboardCopy
              codeFont={true}
              copyText={getCustomBilling.data?.billingUrl}
            />
          )}
        </Grid>
        <Debug data={customBillingPayload} name={"payload"} />
      </Grid>
    );
  }
  return null;
};
